import { useMemo, useCallback } from "react";
import { useSearchParams } from "react-router-dom";
import { z } from "zod";
import { Game, Player, Season, SeasonType, Stat } from "../client";
import { SeasonTypeSchema, StatSchema } from "../client/schemas.gen";
import { LEAGUE } from "../models/NBA";
import { GameSearchData, PlayerSearchData, VideoSearchRanges } from "../models/SearchData";
import { getGameString } from "../utils/utils";
import { LeagueSearchSnapshot } from "../context/LeagueContext";
import { MAX_STACK_SIZE, StackedQuery, areQueriesEquivalent, decodeQueryStack, encodeQueryStack } from "../models/QueryStack";

interface UseSearchUrlStateParams {
  contextLeague: LEAGUE;
  nbaPlayers: Player[];
  wnbaPlayers: Player[];
  games: Game[];
  searchCache?: Record<LEAGUE, LeagueSearchSnapshot | null>;
}

const SearchUrlSchema = z.object({
  type: z.enum(["player", "game"]).nullable().catch(null),
  league: z
    .string()
    .nullish()
    .transform((val) => {
      const lower = val?.toLowerCase();
      if (lower === "wnba") return LEAGUE.WNBA;
      if (lower === "nba") return LEAGUE.NBA;
      return null;
    }),
  player: z.string().nullish().transform((s) => s?.trim() || ""),
  players: z
    .string()
    .nullish()
    .transform((val) => (val ? val.split(",").map((s) => s.trim()).filter(Boolean) : [])),
  game: z.string().nullish().transform((s) => s?.trim() || ""),
  lastN: z.string().nullish().transform((s) => (/^\d+$/.test(s || "") ? s! : "0")),
  stats: z
    .string()
    .nullish()
    .transform((val) => {
      if (!val) return [] as Stat[];
      return val
        .split(",")
        .map((s) => s.trim().toUpperCase())
        .filter((s): s is Stat => (StatSchema.enum as readonly string[]).includes(s));
    }),
  seasons: z
    .string()
    .nullish()
    .transform((val) => (val ? (val.split(",").map((s) => s.trim()) as Season[]) : [])),
  seasonType: z
    .string()
    .nullish()
    .transform((val) =>
      val && (SeasonTypeSchema.enum as readonly string[]).includes(val)
        ? (val as SeasonType)
        : ("Regular Season" as SeasonType)
    ),
  opponent: z.string().nullish().transform((s) => s?.trim() || "0"),
  dateFrom: z.string().nullish().transform((s) => s?.trim() || ""),
  dateTo: z.string().nullish().transform((s) => s?.trim() || ""),
    periods: z
    .string()
    .nullish()
    .transform((val) =>
      val
        ? val
            .split(",")
            .map((s) => Number(s.trim()))
            .filter((n) => !isNaN(n) && n > 0)
        : []
    ),
  order: z.enum(["asc", "desc"]).nullish().transform((val) => val || "desc"),
  q: z.string().nullish().transform((s) => s?.trim() || ""),
});

export const useSearchUrlState = ({
  contextLeague,
  nbaPlayers,
  wnbaPlayers,
  games,
  searchCache,
}: UseSearchUrlStateParams) => {
  const [searchParams, setSearchParams] = useSearchParams();

  const parsed = useMemo(() => {
    const rawParamLeague = searchParams.get("league")?.toLowerCase();
    const isDifferentLeague = Boolean(rawParamLeague && rawParamLeague !== contextLeague);

    let effectiveParams = searchParams;
    if (isDifferentLeague) {
      // In transit between leagues: URL params belong to previous league!
      effectiveParams = searchCache?.[contextLeague]?.searchParamsString
        ? new URLSearchParams(searchCache[contextLeague]!.searchParamsString)
        : new URLSearchParams(`league=${contextLeague}`);
    } else if (!rawParamLeague && searchCache?.[contextLeague]?.searchParamsString) {
      // URL is bare /search but target league has a cached search
      effectiveParams = new URLSearchParams(searchCache[contextLeague]!.searchParamsString);
    }

    const raw = SearchUrlSchema.parse({
      q: effectiveParams.get("q"),
      order: effectiveParams.get("order"),
      type: effectiveParams.get("type"),
      league: effectiveParams.get("league"),
      player: effectiveParams.get("player"),
      players: effectiveParams.get("players") || effectiveParams.get("player"),
      game: effectiveParams.get("game"),
      lastN: effectiveParams.get("lastN"),
      stats: effectiveParams.get("stats") || effectiveParams.get("stat"),
      seasons: effectiveParams.get("seasons") || effectiveParams.get("season"),
      seasonType: effectiveParams.get("seasonType"),
      opponent: effectiveParams.get("opponent"),
      dateFrom: effectiveParams.get("dateFrom"),
      dateTo: effectiveParams.get("dateTo"),
      periods: effectiveParams.get("periods"),
    });

    const searchType = raw.type || (raw.player ? "player" : raw.game ? "game" : null);
    const league = raw.league || contextLeague;

    return {
      ...raw,
      params: effectiveParams,
      setParams: setSearchParams,
      paramLeague: raw.league,
      searchType,
      league,
    };
  }, [searchParams, setSearchParams, contextLeague, searchCache]);

  const activePlayers = parsed.league === LEAGUE.NBA ? nbaPlayers : wnbaPlayers;

  const matchedPlayer = useMemo(() => {
    if (!parsed.player) return null;
    return (
      activePlayers.find(
        (p) =>
          String(p.id) === parsed.player ||
          p.fullName.toLowerCase() === parsed.player.toLowerCase()
      ) || null
    );
  }, [parsed.player, activePlayers]);

  const matchedPlayers = useMemo(() => {
    if (!parsed.players || parsed.players.length === 0) return [];
    return parsed.players
      .map((pIdOrName) =>
        activePlayers.find(
          (p) =>
            String(p.id) === pIdOrName ||
            p.fullName.toLowerCase() === pIdOrName.toLowerCase()
        )
      )
      .filter((p): p is Player => Boolean(p));
  }, [parsed.players, activePlayers]);

  const resolvedPlayerIds = useMemo(() => {
    if (!parsed.players || parsed.players.length === 0) return [];
    return parsed.players
      .map((pIdOrName) => {
        if (/^\d+$/.test(pIdOrName)) return pIdOrName;
        const matched = activePlayers.find(
          (p) => p.fullName.toLowerCase() === pIdOrName.toLowerCase()
        );
        return matched ? String(matched.id) : undefined;
      })
      .filter((id): id is string => Boolean(id));
  }, [parsed.players, activePlayers]);

  // Evaluated variable: primitive string compared by value
  const resolvedPlayerId = !parsed.player
    ? undefined
    : /^\d+$/.test(parsed.player)
    ? parsed.player
    : matchedPlayer
    ? String(matchedPlayer.id)
    : undefined;

  // Memoized: referential stability for downstream form hooks (PlayerSearchForm)
  const playerSearchData = useMemo<PlayerSearchData | null>(() => {
    if (parsed.searchType !== "player" || !parsed.player || !matchedPlayer) return null;

    const availableSeasons =
      VideoSearchRanges[parsed.league]?.[parsed.seasonType] ||
      VideoSearchRanges[parsed.league]["Regular Season"] ||
      [];
    const validSeasons = parsed.seasons.filter((s) => availableSeasons.includes(s));
    const hydratedSeasons =
      validSeasons.length > 0 ? validSeasons : [availableSeasons[0] || (parsed.league === LEAGUE.WNBA ? ("2026" as Season) : ("2026-27" as Season))];

    const hydratedStats =
      parsed.stats.length > 0 ? parsed.stats : [StatSchema.enum.find((v) => v === "PTS")!];

    return {
      league: parsed.league,
      player: matchedPlayer,
      lastNGames: parsed.lastN,
      seasons: hydratedSeasons,
      seasonType: parsed.seasonType,
      oppTeamId: parsed.opponent,
      categories: hydratedStats,
      dateFrom: parsed.dateFrom,
      dateTo: parsed.dateTo,
      periods: parsed.periods,
    };
  }, [parsed, matchedPlayer]);

  // Memoized: referential stability for downstream form hooks (GameSearchForm)
  const gameSearchData = useMemo<GameSearchData | null>(() => {
    if (parsed.searchType !== "game" || !parsed.game) return null;
    const matchedGame = games.find((g) => g.id === parsed.game);
    if (!matchedGame) return null;

    return {
      league: parsed.league,
      game: matchedGame,
      singleSeason:
        parsed.seasons[0] ||
        VideoSearchRanges[parsed.league]?.[parsed.seasonType]?.[0] ||
        (parsed.league === LEAGUE.WNBA ? ("2026" as Season) : ("2026-27" as Season)),
      seasonType: parsed.seasonType,
      categories: parsed.stats,
      players: matchedPlayers,
      periods: parsed.periods,
    };
  }, [parsed, games, matchedPlayers]);

  const decodedFromToken = useMemo(() => {
    if (!parsed.q) return [];
    return decodeQueryStack(parsed.q, parsed.league, activePlayers, games);
  }, [parsed.q, parsed.league, activePlayers, games]);

  const isMultiQuery = Boolean(parsed.q && decodedFromToken.length > 0);

  const stackedQueries = useMemo<StackedQuery[]>(() => {
    if (decodedFromToken.length > 0) {
      return decodedFromToken;
    }
    return [];
  }, [decodedFromToken]);

  const aggregatedPlayerIds = useMemo(() => {
    const ids: string[] = [];
    stackedQueries.forEach((q) => {
      if (q.type === "player" && q.player?.id) {
        ids.push(String(q.player.id));
      } else if (q.type === "game" && q.players) {
        q.players.forEach((p) => ids.push(String(p.id)));
      }
    });
    return Array.from(new Set(ids));
  }, [stackedQueries]);

  const setQueryStack = useCallback(
    (newStack: StackedQuery[]) => {
      if (newStack.length === 0) {
        parsed.setParams({ league: parsed.league });
        return;
      }
      if (newStack.length === 1) {
        const q = newStack[0];
        if (q.type === "player") {
          const searchParamsObj: Record<string, string> = {
            type: "player",
            league: parsed.league,
            player: q.player.id,
            stats: q.categories.join(","),
            seasons: q.seasons.join(","),
            periods: q.periods.join(","),
          };
          if (q.seasonType && q.seasonType !== "Regular Season") {
            searchParamsObj.seasonType = q.seasonType;
          }
          if (q.lastNGames && q.lastNGames !== "0") {
            searchParamsObj.lastN = q.lastNGames;
          }
          if (q.oppTeamId && q.oppTeamId !== "0") {
            searchParamsObj.opponent = q.oppTeamId;
          }
          if (q.dateFrom) searchParamsObj.dateFrom = q.dateFrom;
          if (q.dateTo) searchParamsObj.dateTo = q.dateTo;
          parsed.setParams(searchParamsObj);
        } else {
          const searchParamsObj: Record<string, string> = {
            type: "game",
            league: parsed.league,
            game: q.game.id,
            stats: q.categories.join(","),
            singleSeason: q.singleSeason,
            periods: q.periods.join(","),
          };
          if (q.seasonType && q.seasonType !== "Regular Season") {
            searchParamsObj.seasonType = q.seasonType;
          }
          if (q.players && q.players.length > 0) {
            searchParamsObj.players = q.players.map((x) => x.id).join(",");
          }
          parsed.setParams(searchParamsObj);
        }
        return;
      }
      const token = encodeQueryStack(newStack);
      parsed.setParams({ league: parsed.league, q: token });
    },
    [parsed]
  );

  const removeQueryFromStack = useCallback(
    (queryId: string) => {
      const updated = stackedQueries.filter((q) => q.id !== queryId);
      setQueryStack(updated);
    },
    [stackedQueries, setQueryStack]
  );

  const addQueryToStack = useCallback(
    (query: StackedQuery) => {
      if (stackedQueries.length >= MAX_STACK_SIZE) return;
      if (stackedQueries.some((q) => areQueriesEquivalent(q, query))) return;
      setQueryStack([...stackedQueries, query]);
    },
    [stackedQueries, setQueryStack]
  );

  const activeFilterCount = useMemo(() => {
    if (isMultiQuery) {
      return stackedQueries.length;
    }
    let count = 0;
    if (parsed.searchType === "player") {
      if (parsed.player) count += 1;
      if (parsed.stats.length > 0) count += parsed.stats.length;
      if (parsed.seasons.length > 0) count += 1;
      if (parsed.lastN !== "0") count += 1;
      if (parsed.opponent !== "0") count += 1;
      if (parsed.dateFrom) count += 1;
      if (parsed.dateTo) count += 1;
      if (parsed.periods && parsed.periods.length > 0) count += parsed.periods.length;
    } else if (parsed.searchType === "game") {
      if (parsed.game) count += 1;
      if (matchedPlayers.length > 0) count += matchedPlayers.length;
      if (parsed.stats.length > 0) count += parsed.stats.length;
      if (parsed.seasonType) count += 1;
      if (parsed.periods && parsed.periods.length > 0) count += parsed.periods.length;
    }
    return count;
  }, [isMultiQuery, stackedQueries.length, parsed, matchedPlayers]);

  const { searchSummaryLabel, fullSearchSummaryLabel } = useMemo(() => {
    if (isMultiQuery) {
      const names = stackedQueries.map((q) =>
        q.type === "player" ? q.player.fullName : getGameString(q.game)
      );
      const fullLabel = names.join(" + ");
      const displayLabel =
        names.length > 2
          ? `${names.slice(0, 2).join(" + ")} (+${names.length - 2})`
          : fullLabel;
      return { searchSummaryLabel: displayLabel, fullSearchSummaryLabel: fullLabel };
    }
    if (parsed.searchType === "player") {
      const name = playerSearchData?.player?.fullName || matchedPlayer?.fullName || parsed.player || "";
      return { searchSummaryLabel: name, fullSearchSummaryLabel: name };
    }
    if (parsed.searchType === "game") {
      const gameStr = gameSearchData?.game ? getGameString(gameSearchData.game) : parsed.game || "";
      if (matchedPlayers.length === 0) {
        return { searchSummaryLabel: gameStr, fullSearchSummaryLabel: gameStr };
      }
      const playerNames = matchedPlayers.map((p) => p.fullName);
      const fullLabel = `${gameStr} - ${playerNames.join(", ")}`;
      let displayLabel = fullLabel;
      if (playerNames.length > 2) {
        displayLabel = `${gameStr} - ${playerNames.slice(0, 2).join(", ")} (+${playerNames.length - 2})`;
      }
      return { searchSummaryLabel: displayLabel, fullSearchSummaryLabel: fullLabel };
    }
    return { searchSummaryLabel: "", fullSearchSummaryLabel: "" };
  }, [isMultiQuery, stackedQueries, parsed, playerSearchData, matchedPlayer, gameSearchData, matchedPlayers]);

  const effectiveSearchType = isMultiQuery
    ? stackedQueries[0]?.type || null
    : parsed.searchType;

  return {
    params: parsed.params,
    setParams: parsed.setParams,
    searchType: effectiveSearchType,
    paramLeague: parsed.paramLeague,
    league: parsed.league,
    player: parsed.player,
    players: parsed.players,
    game: parsed.game,
    lastN: parsed.lastN,
    stats: parsed.stats,
    seasons: parsed.seasons,
    seasonType: parsed.seasonType,
    opponent: parsed.opponent,
    dateFrom: parsed.dateFrom,
    dateTo: parsed.dateTo,
    periods: parsed.periods,
    order: parsed.order,
    activePlayers,
    matchedPlayer,
    matchedPlayers,
    resolvedPlayerId,
    resolvedPlayerIds,
    playerSearchData,
    gameSearchData,
    activeFilterCount,
    searchSummaryLabel,
    fullSearchSummaryLabel,
    isMultiQuery,
    stackedQueries,
    aggregatedPlayerIds,
    setQueryStack,
    removeQueryFromStack,
    addQueryToStack,
  };
};
